const SENSITIVE_KEY_PATTERN = /token|password|authorization|cookie|secret|api[-_]?key|credential|session/i;
const PRODUCTION = process.env.NODE_ENV === 'production';
const DEDUPE_WINDOW_MS = 30000;
const MAX_DEPTH = 2;
const MAX_KEYS = 20;

let consoleGuardInstalled = false;
const recentMessages = new Map();

const originalConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

const isDebugOverrideEnabled = () => {
  try {
    return window.localStorage?.getItem('sweetgrass_debug_logs') === 'true';
  } catch (error) {
    return false;
  }
};

export const isDebugLoggingEnabled = () => !PRODUCTION || isDebugOverrideEnabled();

const sanitizeValue = (value, depth = 0) => {
  if (value == null) {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      statusCode: value.statusCode,
    };
  }

  if (typeof value !== 'object') {
    return value;
  }

  if (depth >= MAX_DEPTH) {
    return Array.isArray(value) ? `[Array(${value.length})]` : '[Object]';
  }

  if (Array.isArray(value)) {
    return value.slice(0, 10).map((item) => sanitizeValue(item, depth + 1));
  }

  const sanitized = {};
  Object.entries(value)
    .slice(0, MAX_KEYS)
    .forEach(([key, item]) => {
      sanitized[key] = SENSITIVE_KEY_PATTERN.test(key)
        ? '[REDACTED]'
        : sanitizeValue(item, depth + 1);
    });

  return sanitized;
};

const sanitizeArgs = (args) => args.map((arg) => sanitizeValue(arg));

const safeStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const getMessageKey = (level, args) => {
  const firstArg = args[0];
  const message = typeof firstArg === 'string' ? firstArg : safeStringify(sanitizeValue(firstArg));
  return `${level}:${message}`;
};

const shouldEmit = (level, args) => {
  if (!PRODUCTION || level === 'debug' || level === 'info' || level === 'log') {
    return true;
  }

  const key = getMessageKey(level, args);
  const now = Date.now();
  const lastSeenAt = recentMessages.get(key);

  if (lastSeenAt && now - lastSeenAt < DEDUPE_WINDOW_MS) {
    return false;
  }

  recentMessages.set(key, now);
  return true;
};

const emit = (level, args) => {
  if ((level === 'log' || level === 'info' || level === 'debug') && !isDebugLoggingEnabled()) {
    return;
  }

  if (!shouldEmit(level, args)) {
    return;
  }

  originalConsole[level](...sanitizeArgs(args));
};

export const logger = {
  log: (...args) => emit('log', args),
  info: (...args) => emit('info', args),
  debug: (...args) => emit('debug', args),
  warn: (...args) => emit('warn', args),
  error: (...args) => emit('error', args),
};

export const installProductionConsoleGuard = () => {
  if (!PRODUCTION || consoleGuardInstalled) {
    return;
  }

  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.warn = (...args) => emit('warn', args);
  console.error = (...args) => emit('error', args);

  consoleGuardInstalled = true;
};
